import type { AdminAuditResponse } from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { ACTION_LABELS, actionLabel, riskLevel, titleCase } from "../audit-format.js";
import {
  ActiveFilters,
  CopyButton,
  CsvExportButton,
  DatePresets,
  LoadingState,
  ResultCount,
  RetryBanner,
  readUrlFilters,
  toIsoOrUndefined,
  writeUrlFilters,
} from "../components/ExplorerTools.js";
import { asMoment } from "../format.js";

type Entries = AdminAuditResponse["entries"];
type Entry = Entries[number];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function evidenceValue(value: unknown) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function AuditEvidence({ entry }: { entry: Entry }) {
  const before = isObject(entry.before) ? entry.before : null;
  const after = isObject(entry.after) ? entry.after : null;
  const changedFields =
    before || after
      ? [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter(
          (key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]),
        )
      : [];

  return (
    <div className="audit-evidence-body">
      {entry.justification ? (
        <p className="audit-reason">
          <strong>Reason</strong>
          <span>{entry.justification}</span>
        </p>
      ) : null}
      {changedFields.length > 0 ? (
        <div className="audit-changes">
          <div className="audit-change audit-change-head" aria-hidden="true">
            <span>Field</span>
            <span>Before</span>
            <span>After</span>
          </div>
          {changedFields.map((field) => (
            <div className="audit-change" key={field}>
              <strong>{titleCase(field)}</strong>
              <span data-label="Before">{evidenceValue(before?.[field])}</span>
              <span data-label="After">{evidenceValue(after?.[field])}</span>
            </div>
          ))}
        </div>
      ) : entry.before !== null || entry.after !== null ? (
        <div className="audit-raw-evidence">
          <div>
            <strong>Before</strong>
            <pre>{JSON.stringify(entry.before, null, 2)}</pre>
          </div>
          <div>
            <strong>After</strong>
            <pre>{JSON.stringify(entry.after, null, 2)}</pre>
          </div>
        </div>
      ) : (
        <p className="dim">This event did not change stored data.</p>
      )}
      <dl className="audit-metadata">
        <div>
          <dt>Source IP</dt>
          <dd>{entry.sourceIp ?? "Not recorded"}</dd>
        </div>
        <div>
          <dt>Session</dt>
          <dd className="mono-12">{entry.sessionId ?? "Not recorded"}</dd>
        </div>
      </dl>
    </div>
  );
}

interface Filters {
  actionType: string;
  actorId: string;
  from: string;
  targetId: string;
  to: string;
}

const EMPTY: Filters = { actionType: "", actorId: "", from: "", targetId: "", to: "" };

/**
 * The filters as the client takes them: blanks dropped, and the two dates turned into instants.
 *
 * A `datetime-local` value is wall-clock with no zone. This console consistently labels and
 * renders time in UTC, so the value is interpreted as UTC before it becomes an API instant.
 *
 * Built once and spread at both call sites, so the first page and the next one can never be
 * fetched with different filters.
 */
function asQuery(filters: Filters): Record<string, string> {
  const query: Record<string, string> = {};

  if (filters.actionType) query.actionType = filters.actionType;
  if (filters.actorId) query.actorId = filters.actorId;
  if (filters.targetId) query.targetId = filters.targetId;
  const from = filters.from ? toIsoOrUndefined(`${filters.from}Z`) : undefined;
  const to = filters.to ? toIsoOrUndefined(`${filters.to}Z`) : undefined;
  if (from) query.from = from;
  if (to) query.to = to;

  return query;
}

/**
 * The operator audit log, with the state of its hash chain shown rather than assumed.
 *
 * `verified` is the point of this screen. The rows are interesting; whether they still follow one
 * another is what tells a reader nobody has been rewriting history. Reporting it here means somebody
 * sees it, instead of it being a check that runs somewhere nobody looks.
 *
 * The filters exist because an investigation starts from a question — who did this, what happened
 * to that account — and a log that can only be read from the top turns every question into a
 * scrolling exercise. The chain verdict is unaffected by them: it is computed over the whole log,
 * so narrowing can never make a broken chain look intact.
 */
export function Audit() {
  const [applied, setApplied] = useState<Filters>(() => readUrlFilters(EMPTY));
  const [draft, setDraft] = useState<Filters>(() => readUrlFilters(EMPTY));
  const [data, setData] = useState<AdminAuditResponse | null>(null);
  const [entries, setEntries] = useState<Entries>([]);
  const [selected, setSelected] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const generation = useRef(0);
  const loadMoreController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!selected) return;
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
  }, [selected]);

  function closeDetail() {
    dialog.current?.close?.();
    setSelected(null);
  }

  useEffect(() => {
    const controller = new AbortController();
    const requestGeneration = ++generation.current;
    setData(null);
    setError(null);

    api
      .audit({ signal: controller.signal, ...asQuery(applied) })
      .then((page) => {
        if (generation.current !== requestGeneration) return;
        setData(page);
        setEntries(page.entries);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted || generation.current !== requestGeneration) return;
        setError(caught instanceof ApiError ? caught.message : "Could not load the log.");
      });

    return () => {
      controller.abort();
      loadMoreController.current?.abort();
    };
  }, [applied]);

  /**
   * The next page, appended rather than replacing what is on screen.
   *
   * Somebody paging through a log is following a thread, and replacing the rows would make them
   * lose their place every time they asked for more.
   */
  const loadMore = useCallback(async () => {
    if (!data?.nextCursor || loadingMore) return;
    loadMoreController.current?.abort();
    const controller = new AbortController();
    loadMoreController.current = controller;
    const requestGeneration = generation.current;
    setLoadingMore(true);

    try {
      const page = await api.audit({
        before: data.nextCursor,
        signal: controller.signal,
        ...asQuery(applied),
      });
      if (controller.signal.aborted || generation.current !== requestGeneration) return;
      setEntries((current) => [...current, ...page.entries]);
      setData(page);
    } catch (caught) {
      if (controller.signal.aborted) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load more of the log.");
    } finally {
      if (loadMoreController.current === controller) {
        loadMoreController.current = null;
        setLoadingMore(false);
      }
    }
  }, [applied, data, loadingMore]);

  function search(event: FormEvent) {
    event.preventDefault();
    generation.current += 1;
    loadMoreController.current?.abort();
    loadMoreController.current = null;
    setLoadingMore(false);
    writeUrlFilters(draft);
    setApplied({ ...draft });
  }

  function clear() {
    generation.current += 1;
    loadMoreController.current?.abort();
    loadMoreController.current = null;
    setLoadingMore(false);
    setDraft(EMPTY);
    setApplied({ ...EMPTY });
    writeUrlFilters(EMPTY);
  }

  function removeFilter(key: string) {
    generation.current += 1;
    loadMoreController.current?.abort();
    loadMoreController.current = null;
    setLoadingMore(false);
    const next = { ...applied, [key]: "" };
    setDraft({ ...draft, [key]: "" });
    setApplied(next);
    writeUrlFilters({ ...draft, [key]: "" });
  }

  const activeFilters = Object.entries(applied)
    .filter(([, value]) => value)
    .map(([key, value]) => ({ key, label: key.replace(/Id$/, ""), value }));

  const filtered = Object.values(applied).some(Boolean);

  return (
    <section className="body">
      <form className="filters" onSubmit={search}>
        <div className="field">
          <label htmlFor="filter-action">Action</label>
          <input
            className="input"
            list="audit-action-types"
            id="filter-action"
            onChange={(event) => setDraft({ ...draft, actionType: event.target.value })}
            placeholder="e.g. admin.player.status"
            value={draft.actionType}
          />
          <datalist id="audit-action-types">
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} label={label} value={value} />
            ))}
          </datalist>
        </div>

        <div className="field">
          <label htmlFor="filter-actor">Operator</label>
          <input
            className="input"
            id="filter-actor"
            onChange={(event) => setDraft({ ...draft, actorId: event.target.value })}
            placeholder="Operator id"
            value={draft.actorId}
          />
        </div>

        <div className="field">
          <label htmlFor="filter-target">Target</label>
          <input
            className="input"
            id="filter-target"
            onChange={(event) => setDraft({ ...draft, targetId: event.target.value })}
            placeholder="Player id"
            value={draft.targetId}
          />
        </div>

        <div className="field">
          <label htmlFor="filter-from">From (UTC)</label>
          <input
            className="input"
            id="filter-from"
            onChange={(event) => setDraft({ ...draft, from: event.target.value })}
            type="datetime-local"
            value={draft.from}
          />
        </div>

        <div className="field">
          <label htmlFor="filter-to">To (UTC)</label>
          <input
            className="input"
            id="filter-to"
            onChange={(event) => setDraft({ ...draft, to: event.target.value })}
            type="datetime-local"
            value={draft.to}
          />
        </div>

        <div className="filter-actions">
          <button className="btn" type="submit">
            Filter
          </button>
          {filtered ? (
            <button className="btn btn-quiet" onClick={clear} type="button">
              Clear
            </button>
          ) : null}
        </div>
        <DatePresets
          onSelect={({ from, to }) =>
            setDraft({ ...draft, from: `${from}T00:00`, to: `${to}T23:59` })
          }
        />
      </form>

      <ActiveFilters filters={activeFilters} onClear={clear} onRemove={removeFilter} />

      {error ? (
        <RetryBanner message={error} onRetry={() => setApplied((current) => ({ ...current }))} />
      ) : null}

      {/**
       * What the log came to, announced rather than only drawn. A sighted operator watches the
       * table change; a screen reader is told nothing, because nothing moved focus.
       */}
      <p className="visually-hidden" role="status">
        {data === null
          ? "Loading the audit log."
          : `${entries.length} ${entries.length === 1 ? "entry" : "entries"} shown.`}
      </p>

      {data === null ? (
        <div className="card">
          <LoadingState label="Loading operator activity…" />
        </div>
      ) : (
        <>
          <div className="result-tools">
            <ResultCount count={entries.length} noun="entry" />
            <CsvExportButton
              filename="audit-log.csv"
              headers={[
                "Created at",
                "Risk",
                "Action",
                "Action type",
                "Operator ID",
                "Role",
                "Target type",
                "Target ID",
                "Justification",
                "Source IP",
              ]}
              rows={entries.map((entry) => [
                entry.createdAt,
                riskLevel(entry.actionType),
                actionLabel(entry.actionType),
                entry.actionType,
                entry.adminUserId,
                entry.role,
                entry.targetType,
                entry.targetId,
                entry.justification,
                entry.sourceIp,
              ])}
            />
          </div>
          {data.verified.intact ? (
            <p className="banner banner-ok">Chain intact across {data.verified.checked} entries.</p>
          ) : (
            // Loud on purpose. A broken chain means a row does not follow from the one before it,
            // and the only readings of that are a defect or somebody having written into history.
            <p className="banner" role="alert">
              <span>
                <strong>Chain broken at {data.verified.brokenAt}.</strong> Stop and escalate. Do not
                act on this log.
              </span>
            </p>
          )}

          {entries.length === 0 ? (
            <div className="card">
              <div className="empty empty-action">
                <p>{filtered ? "Nothing matches those filters." : "The log is empty."}</p>
                {filtered ? (
                  <button className="btn btn-quiet" onClick={clear} type="button">
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="card">
              <table className="audit-table mobile-cards">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: "20%" }}>
                      When
                    </th>
                    <th scope="col" style={{ width: "18%" }}>
                      Who
                    </th>
                    <th scope="col" style={{ width: "14%" }}>
                      Role
                    </th>
                    <th scope="col" style={{ width: "26%" }}>
                      Action
                    </th>
                    <th scope="col">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    // The row that broke the chain is marked, so the banner points somewhere.
                    <tr
                      aria-label={`Open audit detail for ${actionLabel(entry.actionType)}`}
                      className={`clickable-row${entry.id === data.verified.brokenAt ? " row-broken" : ""}`}
                      key={entry.id}
                      onClick={(event) => {
                        if ((event.target as HTMLElement).closest("button")) return;
                        setSelected(entry);
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelected(entry);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td className="label-13 dim" data-label="When">
                        {asMoment(entry.createdAt)}
                      </td>
                      <td data-label="Who">
                        <span className="table-id">
                          <span className="mono-12 faint">{entry.adminUserId}</span>
                          <CopyButton value={entry.adminUserId} />
                        </span>
                      </td>
                      {/* The role held at the time, not the one they hold now. */}
                      <td className="label-13" data-label="Role">
                        {entry.role}
                      </td>
                      <td data-label="Action">
                        <div className="audit-event">
                          <span
                            className={`badge badge-audit-${riskLevel(entry.actionType).toLowerCase()}`}
                          >
                            {riskLevel(entry.actionType)}
                          </span>
                          <span className="cell-main">
                            <strong className="label-13">{actionLabel(entry.actionType)}</strong>
                            <span className="mono-12 faint">{entry.actionType}</span>
                          </span>
                        </div>
                      </td>
                      <td data-label="Target">
                        {entry.targetId ? (
                          <span className="cell-main">
                            <strong className="label-13">{titleCase(entry.targetType)}</strong>
                            <span className="table-id">
                              <span className="mono-12 faint">{entry.targetId}</span>
                              <CopyButton value={entry.targetId} />
                            </span>
                          </span>
                        ) : (
                          <span className="dim">{titleCase(entry.targetType) || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selected ? (
            <dialog
              aria-labelledby="audit-detail-title"
              className="round-dialog"
              onCancel={closeDetail}
              onClick={(event) => {
                if (event.target === event.currentTarget) closeDetail();
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeDetail();
              }}
              ref={dialog}
            >
              <div className="round-dialog-head">
                <div>
                  <h2 className="heading-20" id="audit-detail-title">
                    Audit event
                  </h2>
                  <p className="mono-12 faint">{selected.id}</p>
                </div>
                <button className="btn btn-quiet btn-sm" onClick={closeDetail} type="button">
                  Close
                </button>
              </div>
              <div className="round-dialog-body">
                <div className="facts audit-detail-facts">
                  <div className="fact">
                    <div className="fact-label">When</div>
                    <div className="fact-value">{asMoment(selected.createdAt)}</div>
                  </div>
                  <div className="fact">
                    <div className="fact-label">Operator</div>
                    <div className="fact-value id-with-copy">
                      <span className="mono-12">{selected.adminUserId}</span>
                      <CopyButton value={selected.adminUserId} />
                    </div>
                  </div>
                  <div className="fact">
                    <div className="fact-label">Role</div>
                    <div className="fact-value">{selected.role}</div>
                  </div>
                  <div className="fact">
                    <div className="fact-label">Action</div>
                    <div className="fact-value">{actionLabel(selected.actionType)}</div>
                    <div className="mono-12 faint">{selected.actionType}</div>
                  </div>
                  <div className="fact">
                    <div className="fact-label">Target</div>
                    <div className="fact-value">{titleCase(selected.targetType) || "—"}</div>
                    {selected.targetId ? (
                      <div className="id-with-copy">
                        <span className="mono-12 faint">{selected.targetId}</span>
                        <CopyButton value={selected.targetId} />
                      </div>
                    ) : null}
                  </div>
                </div>
                <AuditEvidence entry={selected} />
              </div>
            </dialog>
          ) : null}

          {data.nextCursor ? (
            <button
              className="btn btn-quiet load-more-button"
              disabled={loadingMore}
              onClick={() => void loadMore()}
              type="button"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          ) : null}
        </>
      )}

      <p className="hint">
        Reads are recorded as well as writes: opening a player is itself an entry. The chain is
        verified across the whole log, not across what is filtered here.
      </p>
    </section>
  );
}
